import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  TrendingDown,
  HandCoins,
  CircleCheck,
  CircleDollarSign,
  Timer,
  AlertOctagon,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface LoanData {
  summary: {
    totalApplications: number;
    approved: number;
    disbursed: number;
    active: number;
    completed: number;
    rejected: number;
    outstandingBalance: number;
    expectedInterest: number;
    actualInterest: number;
    overdue: number;
  };
  statusBreakdown: Array<{
    status: string;
    count: number;
  }>;
  approvalProgress: Array<{
    stage: string;
    count: number;
  }>;
  loanAging: Array<{
    range: string;
    count: number;
    totalAmount: number;
  }>;
  outstandingBalances: Array<{
    loanId: string;
    memberName: string;
    amount: number;
    outstanding: number;
    status: string;
    daysOverdue: number;
  }>;
}

const PIE_COLORS = ["#009A44", "#00C853", "#4CAF50", "#81C784", "#A5D6A7", "#C8E6C9", "#E53935", "#FF9800"];

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    red: "bg-red-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    purple: "bg-purple-50",
    gray: "bg-gray-100",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    red: "text-red-600",
    blue: "text-blue-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const statusBadge: Record<string, string> = {
  active: "bg-[#009A44]/10 text-[#009A44]",
  completed: "bg-blue-50 text-blue-600",
  overdue: "bg-red-50 text-red-600",
  pending: "bg-amber-50 text-amber-600",
  rejected: "bg-gray-100 text-gray-600",
};

const agingColors: Record<string, string> = {
  "0-30 days": "bg-[#009A44]/10 text-[#009A44]",
  "31-60 days": "bg-amber-50 text-amber-600",
  "61-90 days": "bg-orange-50 text-orange-600",
  "90+ days": "bg-red-50 text-red-600",
};

export default function LoanReport() {
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_REPORT_FILTERS);

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-loans", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.dateStart) params.dateStart = filters.dateStart;
      if (filters.dateEnd) params.dateEnd = filters.dateEnd;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.type && filters.type !== "all") params.type = filters.type;

      const { data } = await api.get("/reports/loans", { params });
      return data?.data as LoanData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || {
    totalApplications: 0,
    approved: 0,
    disbursed: 0,
    active: 0,
    completed: 0,
    rejected: 0,
    outstandingBalance: 0,
    expectedInterest: 0,
    actualInterest: 0,
    overdue: 0,
  };
  const statusBreakdown = rawData?.statusBreakdown || [];
  const approvalProgress = rawData?.approvalProgress || [];
  const loanAging = rawData?.loanAging || [];
  const outstandingBalances = rawData?.outstandingBalances || [];

  const exportColumns = [
    { key: "loanId", label: "Loan ID" },
    { key: "memberName", label: "Member" },
    { key: "amount", label: "Amount" },
    { key: "outstanding", label: "Outstanding" },
    { key: "status", label: "Status" },
    { key: "daysOverdue", label: "Days Overdue" },
  ];

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Loan Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Loan applications, disbursements, and outstanding balances
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load loan report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <CreditCard className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Loan Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Loan applications, disbursements, and outstanding balances
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["dateStart", "dateEnd", "month", "year", "status", "type"]}
        />

        {/* Summary Cards - Row 1 */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Total Applications"
            value={summary.totalApplications}
            icon={<CreditCard className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Approved (3/3)"
            value={summary.approved}
            icon={<CheckCircle2 className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Disbursed"
            value={summary.disbursed}
            icon={<HandCoins className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Active"
            value={summary.active}
            icon={<Clock className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
        </div>

        {/* Summary Cards - Row 2 */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <SummaryCard
            title="Completed"
            value={summary.completed}
            icon={<CircleCheck className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Rejected"
            value={summary.rejected}
            icon={<XCircle className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
          <SummaryCard
            title="Outstanding Balance"
            value={summary.outstandingBalance}
            icon={<CircleDollarSign className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
          <SummaryCard
            title="Expected Interest"
            value={summary.expectedInterest}
            icon={<TrendingDown className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Actual Interest"
            value={summary.actualInterest}
            icon={<BarChart3 className="h-5 w-5" />}
            accentColor="purple"
            loading={loading}
          />
        </div>

        {/* Overdue + Charts Row */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Overdue Card */}
          <SummaryCard
            title="Overdue Loans"
            value={summary.overdue}
            icon={<AlertOctagon className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />

          {/* Approval Progress */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <Timer className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Approval Progress</h3>
              </div>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-12" />
                    </div>
                  ))}
                </div>
              ) : approvalProgress.length === 0 ? (
                <div className="py-8 text-center">
                  <Timer className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                  <p className="text-xs text-gray-500">No approval data</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvalProgress.map((item) => (
                    <div key={item.stage} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{item.stage}</span>
                      <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Loan Status Breakdown Pie Chart */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                <BarChart3 className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              <h3 className="text-sm font-semibold">Loan Status Breakdown</h3>
            </div>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : statusBreakdown.length === 0 ? (
              <div className="py-12 text-center">
                <BarChart3 className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No status data available</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="count"
                      nameKey="status"
                    >
                      {statusBreakdown.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loan Aging Table */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                <Timer className="h-3.5 w-3.5 text-[#009A44]" />
              </div>
              <h3 className="text-sm font-semibold">Loan Aging</h3>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            ) : loanAging.length === 0 ? (
              <div className="py-8 text-center">
                <Timer className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                <p className="text-xs text-gray-500">No aging data available</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {loanAging.map((item) => (
                  <div
                    key={item.range}
                    className="rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                          agingColors[item.range] || "bg-gray-100 text-gray-600"
                        )}
                      >
                        {item.range}
                      </span>
                      <span className="text-lg font-bold text-gray-900">{item.count}</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Total: {formatCurrency(item.totalAmount)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Outstanding Balances Table */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <CircleDollarSign className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Outstanding Balances</h3>
              </div>
              <ReportExport
                data={outstandingBalances}
                filename="loan-report"
                title="Loan Report"
                columns={exportColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : outstandingBalances.length === 0 ? (
              <div className="py-12 text-center">
                <CreditCard className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No outstanding balances found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Loan ID</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Member</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Amount</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Outstanding</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Status</th>
                      <th className="pb-2 font-medium text-gray-500 text-right">Days Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingBalances.map((loan) => (
                      <tr
                        key={loan.loanId}
                        className="border-b last:border-b-0 hover:bg-gray-50/50"
                      >
                        <td className="py-3 pr-4 font-mono text-xs text-gray-500">
                          {loan.loanId}
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {loan.memberName}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                          {formatCurrency(loan.amount)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-amber-600">
                          {formatCurrency(loan.outstanding)}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              statusBadge[loan.status] || "bg-gray-100 text-gray-600"
                            )}
                          >
                            {loan.status}
                          </span>
                        </td>
                        <td
                          className={cn(
                            "py-3 text-right font-semibold",
                            loan.daysOverdue > 90
                              ? "text-red-600"
                              : loan.daysOverdue > 60
                              ? "text-orange-600"
                              : loan.daysOverdue > 30
                              ? "text-amber-600"
                              : "text-gray-600"
                          )}
                        >
                          {loan.daysOverdue > 0 ? `${loan.daysOverdue}d` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
